/**
 * Der Store der SPA: Context + `useReducer`, das Abonnement des Ereignisstroms und die Ausfuehrung
 * der vom Reduzierer vorgemerkten Nachladeaufrufe.
 *
 * Arbeitsteilung: der Reduzierer (`reduzierer.ts`) ENTSCHEIDET und ist deshalb eine reine Funktion;
 * diese Komponente FUEHRT AUS. Deshalb steht die Zusicherung "ein Ereignis loest genau EINEN
 * gezielten Nachladeaufruf aus" auch hier und nicht am Reduzierer - dort waere sie nicht pruefbar.
 */
import { createContext, useCallback, useContext, useEffect, useReducer, useRef } from "react";
import type { ReactNode } from "react";
import { ANFANGSZUSTAND, reduzierer } from "./reduzierer.ts";
import type { Aktion, SseEreignis, Speicherzustand } from "./reduzierer.ts";
import { meldeFehler, meldeNachladeFehler } from "./fehlerStapel.ts";
import {
  KONFLIKT_STAND_SATZ,
  LIVE_NACHLADEN_SATZ,
  fehlschlaege,
  meldeBestandTeilFehler,
  mitTeilFehler,
  versuche,
} from "./bestandTeilFehler.ts";
import type { BestandTeilFehler } from "./bestandTeilFehler.ts";
import { uebernahmeLaeuft } from "./konflikte.ts";
import { fehlertext } from "../lib/fehlertext.ts";
import { ereignisseAbonnieren } from "../api/ereignisse.ts";
import { SitzungsFehler, anfrageOderNull } from "../api/basis.ts";
import {
  bestellungLaden,
  bestellungenLaden,
  SSE_ENTITAET as BESTELLUNG_ENTITAET,
} from "../api/bestellungen.ts";
import { jtlBestellungenRohLaden } from "../api/jtlBestellungen.ts";
import { lieferantLaden, SSE_ENTITAET as LIEFERANT_ENTITAET } from "../api/lieferanten.ts";
import {
  kurssicherungLaden,
  kurssicherungenLaden,
  SSE_ENTITAET as KURSSICHERUNG_ENTITAET,
} from "../api/kurssicherung.ts";
import { taskVorlagenLaden } from "../api/aufgabenVorlagen.ts";
import { ladeVorlagen } from "../api/zahlungsvorlagen.ts";
import { ladeFreigaben } from "../api/lieferantenZahlungsziele.ts";
import { konfigurationLesen } from "../api/konfiguration.ts";
import type { Order, Supplier } from "../lib/types.ts";
import type { FxDeal } from "../lib/fx.ts";
import { bestellungenZusammensetzen } from "../lib/bestandZusammensetzen.ts";
import { bestellungenAusQuellen, istZahlungsrolle, lieferantenKennungen, vorgabeZustaende } from "../lib/bestandQuellen.ts";
import { normalizeOrderListColumns, setStatusDefinitions } from "../lib/status.ts";
import type { BoardColumnLike } from "../lib/status.ts";
import { normalisierteStatus } from "./statusNormalisierung.ts";
import type { OrderListColumn } from "../config/board.ts";
import { lesen } from "./adresse.ts";
import type { BoardStateSelection } from "./adresse.ts";

/**
 * Was ein Bestandslauf liefert: die Felder des Bestands, die in den Store kommen, plus optional `teilFehler` (Fehleranzeige
 * Paket 2, Task 5) - was dabei einzeln ausfiel (Vorlagen, Lieferanten, Freigaben, siehe `bestandTeilFehler.ts`). Der
 * Teilfehler gelangt NICHT in den Store: `laufAbwickeln` loest ihn vor dem Dispatch heraus und meldet ihn erst, nachdem
 * der Lauf uebernommen ist. Der Schluessel fehlt, wenn nichts ausfiel.
 */
export type BestandLadeErgebnis = Partial<Speicherzustand> & { teilFehler?: BestandTeilFehler };

/**
 * Die Aussenkontakte des Stores, als Prop hereingereicht statt fest importiert: der Test braucht sie
 * ohne Netz, und der Merge mit Phase 4a hat damit genau eine Stelle zum Umhaengen.
 */
export interface SpeicherDienste {
  /**
   * Abonniert den SSE-Strom. Rueckgabe: Abbestellfunktion (MUSS beim Abbau gerufen werden).
   *
   * `aufFehler` (Befund B40, Nachmessung R5): wird bei jedem Verbindungsfehler gerufen. Der Effekt
   * unten reagiert NUR, wenn es ein `SitzungsFehler` ist (401 auf dem Strom) - jeder andere Fehler
   * (Netz weg, 5xx, ...) bleibt beim eingebauten Wiederverbindungsversuch von `ereignisseAbonnieren`
   * ohne weiteres Zutun des Stores. Optional, damit Tests, die `aufFehler` nicht brauchen, ihn
   * weiterhin weglassen koennen (gleiche Haltung wie bei `bestandLaden` oben).
   *
   * `aufLuecke` (Lueckenausgleich, Spec 2026-10-24): der Strom meldet, dass Ereignisse verloren sein koennen, die
   * niemand nachliefert. Der Store vermerkt das fuer die Leiste (`verbindungsluecke`).
   */
  ereignisseAbonnieren: (
    aufEreignis: (ereignis: SseEreignis) => void,
    aufFehler?: (fehler: unknown) => void,
    aufLuecke?: () => void,
  ) => () => void;
  /**
   * Laedt genau EINEN Datensatz nach. `null` heisst: den gibt es nicht mehr (404) - der Reduzierer
   * nimmt ihn dann aus der Liste. Ein abgelehntes Promise heisst: Nachladen fehlgeschlagen, der
   * bisherige Stand bleibt stehen, und der Store meldet es im Fehlerstapel (siehe den Nachlade-Effekt in
   * `Speicher`, Fehleranzeige Paket 2, Task 5).
   */
  nachladen: (entitaet: string, id: string) => Promise<unknown>;
  /**
   * Optional: laedt den Anfangsbestand. Seit Task 8 unbelegt, seit Task 1 (Phase 6) mit
   * `STANDARD_DIENSTE.bestandLaden` besetzt (zwei Zweige, siehe dort) - `optional` bleibt es
   * trotzdem: Tests, die den Anfangsbestand nicht brauchen, koennen das Feld weiterhin weglassen.
   *
   * WANN ES GERUFEN WIRD: sobald `zustand.nutzer` von `null` auf einen Nutzer wechselt, also NACH
   * der Anmeldung - nie bei der blossen Montage. Ein Test, der diesen Dienst belegt und Daten
   * erwartet, muss deshalb entweder `anfangszustand={{ nutzer: â€¦ }}` mitgeben oder sich anmelden.
   * Begruendung samt Fehlerbild am Effekt in `Speicher` unten.
   *
   * `states` (Auftrag 06, "Zustandsmenge statt Archivwert" - vorher `archiv`): OPTIONAL, gereicht an
      * `jtlBestellungenRohLaden` - fehlt es, gilt dessen/der Route Vorgabe (`active`). Derselbe Dienst
      * bedient ZWEI Aufrufer: das Erstladen (Effekt unten, MIT dem ueber `ersteZustandswahl` aus Rolle
      * und Adresse ermittelten Wert) UND `bestandNeuLaden` (siehe `SpeicherWert` unten), das ihn beim
      * Zustandswechsel im Board ebenfalls explizit mitgibt.
      */
     bestandLaden?: (
      states?: BoardStateSelection,
      optionen?: { frisch?: boolean },
    ) => Promise<BestandLadeErgebnis>;
  /**
   * Optional: der TEILLAUF fuer einen reinen Zustandswechsel im Board (`bestandNeuLaden`). Laedt nur,
   * was von `states` abhaengt - `GET /api/jtl/orders?state=...` und was zwingend daran haengt
   * (Lieferanten und Freigaben der neu aufgetauchten Bestellungen, soweit `kontext.aktuell` sie noch
   * nicht fuehrt). Hub-Zeilen (`/api/orders`), Board-Spalten, Vorlagen und Kurssicherungen haengen
   * NICHT vom Zustand ab und kommen weder neu noch doppelt.
   *
   * `kontext.aktuell`: der Speicherzustand zum Aufrufzeitpunkt. `kontext.signal`: bricht ab, sobald ein
   * neuerer Lauf gestartet ist - der Dienst lehnt dann ab, der Store verwirft die Ablehnung still.
   *
   * Fehlt der Dienst, faellt `bestandNeuLaden` auf `bestandLaden` zurueck (voller Lauf).
   */
  bestandTeilLaden?: (
    states: BoardStateSelection | undefined,
    kontext: { aktuell: Speicherzustand; signal: AbortSignal; aktuellerStore: () => Speicherzustand },
  ) => Promise<BestandLadeErgebnis>;
}

/**
 * SSE-Entitaetsname -> Nachladefunktion. `Map` statt Objektliteral, gleiche Begruendung wie bei
 * `ENTITAET_ZU_FELD` in `reduzierer.ts`: der Schluessel stammt aus einem SSE-Ereignis.
 *
 * Die Schluessel sind seit der Fix-Runde 1 die EINZAHLFORMEN des Backends ("order", "supplier",
 * "fxDeal") - vorher stand hier die Mehrzahl, und deshalb fand KEIN eingehendes Ereignis je seinen
 * Nachlader: jedes lief in die Ablehnung unten. Sie kommen aus derselben Quelle wie
 * `ENTITAET_ZU_FELD` im Reduzierer; `test/komponenten/api/entitaeten.test.ts` haelt beide gegen
 * `BROADCAST_ENTITIES` aus `backend/src/sse/broadcast.ts`.
 *
 * Alle drei laden als HINTERGRUNDANFRAGE (`hintergrund = true`, `x-bestellhub-hintergrund: 1`): das
 * Ereignis eines Kollegen loest sie aus, nicht der Mensch an diesem Tab - das Backend soll dafuer
 * `users.last_seen_at` nicht fortschreiben. Der Nachladeweg ist auch der des Knopfes "neu laden" im
 * Konfliktbanner (`konfliktUebernehmen`, `reduzierer.ts`); dessen Klick zaehlt dadurch selbst nicht
 * als Aktivitaet - bewusst hingenommen, der Verweis traegt nicht, wer das Nachladen veranlasst hat.
 */
const NACHLADER = new Map<string, (id: string) => Promise<unknown>>([
  [BESTELLUNG_ENTITAET, (id) => bestellungLaden(id, undefined, true)],
  [LIEFERANT_ENTITAET, (id) => lieferantLaden(id, undefined, true)],
  [KURSSICHERUNG_ENTITAET, (id) => kurssicherungLaden(id, undefined, true)],
]);

/**
 * Fuer die Entitaet eines Ereignisses gibt es keinen Nachlader (Fehleranzeige Paket 2, Fix-Runde 1, R-T5b). Das ist
 * ein anderer Fall als ein gescheitertes Nachladen: das Backend sendet auch Ereignisse zu `config`,
 * `responsibilityModule` und `taskTemplate`, die kein Client einzeln nachlaedt; es wurde nichts versucht, also ist
 * nichts gescheitert. Der Nachlade-Effekt erkennt die Ablehnung an DIESEM TYP (nie am Text der Meldung) und bleibt
 * still - sonst bekaeme jeder andere Nutzer einen Eintrag, sobald ein Admin ein Modul oder eine Vorlage aendert.
 * Eine Ablehnung bleibt es trotzdem (und nicht etwa ein stilles `undefined`), siehe `STANDARD_DIENSTE.nachladen`.
 */
export class KeinNachladerFehler extends Error {
  constructor(entitaet: string) {
    super(`Kein Nachlader fuer Entitaet "${entitaet}"`);
    this.name = "KeinNachladerFehler";
  }
}

/**
 * Anfangsbestand (Task 1, Phase 6). ZWEI Zweige - der Grund steht in `vite.config.ts:15-28`: der
 * Baseline-Rundlauf bedient die SPA ueber den Proxy aus `server.js` (nur `/api/data`,
 * `server.js:724`), der Produktionsweg ueber das Domaenen-Backend (nur Domaenenrouten). Ein Lader,
 * der nur eine Seite kann, ist auf der anderen tot.
 *
 * DIE SONDIERUNG SELBST UEBERSPRINGEN, WENN `VITE_ZIEL_BACKEND=domaene` GESETZT IST (deploy/
 * Dockerfile, Stufe `frontend-build`): im echten Produktions-Docker-Image gibt es `server.js` gar
 * nicht, `/api/data` ist dort ein GARANTIERTER 404 (`backend/src/app.ts`, genereller Fallback) -
 * und der Browser protokolliert jede 4xx/5xx-Antwort in der DevTools-Konsole, unabhaengig davon,
 * wie sauber `anfrageOderNull` den Fehler abfaengt. `import.meta.env.PROD` waere hierfuer das
 * falsche Signal: `vite build` setzt es auch fuer den Bau, den der visuelle Testharness
 * (`frontend/test/visual/harness.ts`) per `vite preview` gegen `server.js` prueft - dort MUSS
 * Zweig 1 weiterhin laufen. `VITE_ZIEL_BACKEND` steht deshalb nur im Produktionsbau.
 *
 * `states` (Auftrag 06, "Zustandsmenge statt Archivwert" - vorher `archiv`, Auftrag "Serverseitiger
  * Archivfilter + Ladeanzeige", 2026-08-27): geht NUR in Zweig 2 an `jtlBestellungenRohLaden` - Zweig 1
  * (der lokale Bestands-Server) liefert weiterhin immer alles in einem Aufruf, ohne serverseitigen
  * Zustandsfilter (er kennt `order_extras` nicht, siehe dort); ein Aufrufer, der ihn braucht, filtert
  * dort wie bisher clientseitig weiter.
  */
async function bestandLadenEinzeln(states?: BoardStateSelection): Promise<BestandLadeErgebnis> {
  const liste = (w: unknown) => (Array.isArray(w) ? w : []);
  // Zweig 1: Bestands-Server (server.js) - liefert alles in einem Aufruf, wie `load()` im Original
  // (`index.html:5876-5913`, `normalizedData`). Im Produktionsbau (`VITE_ZIEL_BACKEND=domaene`)
  // bleibt diese Sondierung ganz aus - siehe Kommentar oben.
  const alt =
    import.meta.env.VITE_ZIEL_BACKEND === "domaene"
      ? null
      : await anfrageOderNull<Record<string, unknown>>("/api/data");
  if (alt) {
    const boardSpalten = liste(alt.boardColumns) as BoardColumnLike[];
    return {
      // index.html:5831-5835, siehe `normalisierteStatus` - im Bestand Teil von `migrateOrders`,
      // das `load()` VOR jeder Ansicht ueber die Bestellungen laufen laesst.
      bestellungen: normalisierteStatus(
        liste(alt.orders) as Order[],
        setStatusDefinitions(boardSpalten),
      ),
      lieferanten: liste(alt.suppliers) as Supplier[],
      fxDeals: liste(alt.fxDeals) as FxDeal[],
      // `boardSpalten` bleibt hier ROH, ohne `ensureCompletedBoardColumn`: das Feld hat in
      // `frontend/src/` genau einen Leser, `components/App/App.tsx:2607`
      // (`setStatusDefinitions(zustand.boardSpalten)`), und der wendet `ensureCompletedBoardColumn`
      // intern selbst an (`lib/status.ts:264`). Ein zweiter Durchlauf hier waere doppelt gemoppelt.
      boardSpalten,
      // index.html:5882 (`normalizedData`): `orderListColumns` lief dort schon durch
      // normalizeOrderListColumns. Der Port hatte diese Zeile verloren (reine Portierungsluecke) -
      // hier nachgezogen.
      orderListSpalten: normalizeOrderListColumns(alt.orderListColumns) as OrderListColumn[],
      taskVorlagen: liste(alt.taskTemplates),
    };
  }
  // Zweig 2: Domaenenrouten (Phase-4a/4b-Backend). Fehlende Leserouten fallen benannt zurueck:
  // `GET /api/config/:key` IST SEIT DEM 2026-08-14 GEBAUT (`config.ts`) - bis dahin gab es nur PATCH,
  // `konfigurationLesen` lieferte immer `null` und `boardSpalten` blieb `[]`. Der Rueckfall unten
  // bleibt trotzdem noetig: app_config ist heute leer, ein unbekannter Schluessel antwortet weiterhin
  // mit 404 -> `null`. Eine Suppliers-LISTE existiert nicht -> Einzelabrufe
  // je in den Bestellungen vorkommender `supplierId` (`GET /api/suppliers/:kLieferant`,
  // `suppliers.ts:38`). ENTFALLEN (Auftrag â€žSendungsabruf beim App-Start entfernen", 2026-08-22):
  // hier stand als dritter von fuenf Eintraegen im `Promise.all` unten - der vierte tatsaechliche
  // `fetch`-Aufruf dieses Ladelaufs, nach der `/api/data`-Sondierung, `bestellungenLaden()` und
  // `kurssicherungenLaden()` - ein Aufruf gegen `GET /api/dsv/shipments` OHNE `q` (der Sammelabruf
  // beim App-Start). Er lief BIS ZUM 22.08.2026 (`fc0fc26`) wegen `SUCHE_MINDESTLAENGE = 2` immer in
  // ein leeres Array; SEIT `fc0fc26` holte derselbe Aufruf bei JEDEM App-Start bis zu 50 echte
  // Saetze, die niemand las. Das machte den Sammelabruf nicht richtig, sondern erst spuerbar teuer -
  // unabhaengig vom Rueckgabewert stand er ohnehin in der falschen Antwortform da (siehe
  // Kopfkommentar `api/sendungen.ts`, Formbruch `Sendung` vs. `SendungsTreffer`). Die Aufloesung je
  // Bestellung laeuft seither ausschliesslich ueber die Huelle um `GET /api/orders/:orderId/shipment`
  // (`api/sendungen.ts`), gerufen vom Drawer.
  // Die Hub-Zeilen haengen NICHT von `states` ab (`/api/orders` kennt keinen state-Parameter) und sind
  // der grosse Brocken (~25 MB): ihr Promise bleibt fuer `bestandTeilLaden` stehen, damit ein
  // Zustandswechsel sie nicht erneut holt.
  const hubLauf: Promise<Order[]> = bestellungenLaden().then((zeilen) => {
    // Frische Hub-Zeilen ersetzen den Zwischenspeicher - es sei denn, ein neuerer Lauf ist schon dran.
    if (hubZeilenLauf === hubLauf) hubStandErsetzen(zeilen);
    return zeilen;
  });
  hubZeilenLauf = hubLauf;
  hubLauf.catch(() => { // still: der Fehler läuft über denselben Promise in den Ladelauf unten (Promise.all) und von dort zum Aufrufer; hier wird nur der Zwischenspeicher freigegeben, damit der nächste Lauf neu lädt
    if (hubZeilenLauf === hubLauf) hubZeilenLauf = null;
  });
  const [hubZeilen, fxDeals, spaltenWert, taskVorlagen, vorlagenVersuch] = await Promise.all([
    hubLauf,
    kurssicherungenLaden(),
    konfigurationLesen("boardColumns"),
    taskVorlagenLaden(),
    // Fix-Runde 1 (Task 7, Important 3): wie ExtrasFormular.tsx's eigener ladeVorlagen()-Aufruf darf
    // ein Fehlschlag hier NICHT den gesamten Erstladelauf reissen - Promise.all lehnt sonst GESAMT ab
    // und das Board bleibt komplett leer, obwohl die vier anderen Eintraege erfolgreich geladen haben.
    // Der leere Vorlagen-Snapshot darf nur die Auswahl in Lieferantenformularen betreffen; die
    // Freigabe- und Bestellrouten von Task 13 laden und setzen Zahlungsziele separat.
    // Der Fehlschlag faellt aber nicht mehr still: `versuche` behaelt die Ursache, und der Speicher meldet sie als
    // Teilfehler, sobald er den Lauf uebernommen hat (Fehleranzeige Paket 2, Task 5, L2).
    versuche(ladeVorlagen()),
  ]);
  const vorlagen = vorlagenVersuch.ok ? vorlagenVersuch.wert : [];

  /**
   * SEIT DEM 2026-08-15 KOMMEN DIE BESTELLUNGEN AUS JTL, nicht mehr aus `orders`. Die Hub-Zeilen
   * von oben liefern nur noch, was JTL nicht kennt (Board-Status, Zahlungen, Dokumente, Aufgaben)
   * und werden ueber `jtl_order_id` dazugemischt - siehe `api/jtlBestellungen.ts`.
   *
   * Der Grund in einem Satz: die Anlagemaske schrieb in `orders` nur den Lieferanten, alles andere
   * fiel weg, und im Board stand eine Zeile ohne Inhalt. Statt die fehlenden Felder nun zusaetzlich
   * im Tool zu fuehren, kommen sie von dort, wo sie ohnehin stehen.
   *
   * KEIN Rueckfall auf die Hub-Zeilen, wenn JTL ausfaellt: dann zeigt das Board nichts, statt einen
   * Ausschnitt zu zeigen, der wie der vollstaendige Bestand aussieht. Ein halber Bestand, dem man
   * das nicht ansieht, ist schlechter als ein sichtbarer Ausfall.
   */
  /**
   * index.html:5831-5835 (`migrateOrders`), siehe `normalisierteStatus` in `state/statusNormalisierung.ts`.
   *
   * Die ganze Folge von den Rohquellen zu Bestellungen und Board-Spalten ist seit dem KI-Zugang (Stufe 1) EINE reine Funktion,
   * `lib/bestandQuellen.ts#bestellungenAusQuellen`: Spalten aus dem Konfigurationswert (`boardSpaltenAusKonfiguration`), dann
   * Zusammenfuehren und Normalisieren (`lib/bestandZusammensetzen.ts#bestellungenZusammensetzen`). Die Reihenfolge ist die Aussage:
   * `zusammenfuehren` kennt die Board-Spalten nicht, und der Rueckfall des Status ist die ERSTE Spalte - der Bestand liest ihn
   * ebenfalls erst im Ladepfad, nachdem `DB.boardColumns` steht (`load()`, index.html:5902). Der Server faehrt dieselbe Funktion;
   * der Abruf (`jtlBestellungenRohLaden`) bleibt hier. Zwischen dem Ergebnis und dem Rueckgabewert unten folgt KEIN weiterer Schritt
   * an `bestellungen` und `boardSpalten` (`backend/test/ki-bestand-quelltext.test.ts` prueft das am Quelltext).
   */
  const { bestellungen, boardSpalten } = bestellungenAusQuellen(
    await jtlBestellungenRohLaden(states),
    hubZeilen,
    spaltenWert,
  );

  // Welche Lieferanten zu laden sind: `lib/bestandQuellen.ts#lieferantenKennungen` (derselbe Schritt auf dem Server).
  const ids = lieferantenKennungen(bestellungen);
  // Ein Lieferant, der sich nicht laden laesst, faellt aus der Liste - aber nicht mehr still: die gescheiterten
  // werden gezaehlt und als EIN Teilfehler gemeldet (L2/L5). `null` (404) ist kein Fehlschlag: den Lieferanten
  // gibt es nicht, er faellt ohne Meldung weg.
  const lieferantenVersuche = await Promise.all(ids.map((id) => versuche(lieferantLaden(id))));
  const lieferanten = lieferantenVersuche.flatMap((v) => (v.ok && v.wert !== null ? [v.wert] : []));
  // Freigabelisten je Lieferant. Bewusst NACH dem Promise.all: welche Lieferanten ueberhaupt
  // vorkommen, ergibt sich erst aus den geladenen Bestellungen. Jede Liste faengt ihren eigenen
  // Fehler ab - dieselbe Haltung wie bei ladeVorlagen() oben: ein fehlgeschlagener Nebenbestand darf
  // nicht das Board leeren. Ohne Freigaben bietet das Zahlungsziel-Dropdown keine Option an; der Speicher sagt
  // das als Teilfehler (L2/L5), ein Eintrag fuer alle gescheiterten Listen des Laufs.
  const kLieferanten = [...new Set(bestellungen.map((b) => b.kLieferant).filter(Boolean))];
  const freigabenPaare = await Promise.all(
    kLieferanten.map(async (k) => [String(k), await versuche(ladeFreigaben(Number(k)))] as const),
  );
  const freigaben = Object.fromEntries(freigabenPaare.map(([k, v]) => [k, v.ok ? v.wert : []] as const));
  const teilFehler: BestandTeilFehler = {};
  if (!vorlagenVersuch.ok) teilFehler.vorlagen = vorlagenVersuch.ursache;
  const lieferantenFehler = fehlschlaege(lieferantenVersuche);
  if (lieferantenFehler) teilFehler.lieferanten = lieferantenFehler;
  const freigabenFehler = fehlschlaege(freigabenPaare.map(([, v]) => v));
  if (freigabenFehler) teilFehler.freigaben = freigabenFehler;
  return mitTeilFehler({
    bestellungen,
    fxDeals,
    lieferanten,
    taskVorlagen,
    vorlagen,
    freigaben,
    boardSpalten,
    // BENANNTE NAHT: `orderListSpalten` bleibt hier unbelegt (Rueckfall auf `ANFANGSZUSTAND`,
    // `[]`) - es gibt in diesem Zweig keine Leseroute fuer `orderListColumns`,
    // `konfigurationLesen` wird oben nur mit `"boardColumns"` gerufen. Eine kuenftige Leseroute
    // MUSS ihren Wert durch `normalizeOrderListColumns` (siehe Zweig 1 oben) schicken, bevor er
    // hier landet - genau wie im Original (`index.html:5882`). Der Aufbau der Route selbst ist
    // NICHT Teil dieses Auftrags.
    // Beleg: test/komponenten/state/bestandLaden.test.tsx, Fall D2.
  }, teilFehler);
}

/**
 * Das Promise der Hub-Zeilen (`GET /api/orders`) aus dem letzten vollen Lauf. `null`: es gab noch
 * keinen (oder der letzte scheiterte, oder Zweig 1 lief) - dann faellt `bestandTeilLaden` auf den
 * vollen Lauf zurueck. Modulweit wie `STANDARD_DIENSTE` selbst; Tests leeren es ueber
 * `bestandZwischenspeicherLeeren`.
 */
let hubZeilenLauf: Promise<Order[]> | null = null;

/**
 * Der lebende Stand der Hub-Zeilen, je `jtlOrderId`: die Zeilen des letzten vollen Laufs, fortgeschrieben
 * mit jeder Aenderung, die der Store uebernimmt (`hubStandFortschreiben`, `hubZeileEntfernen`). Er darf
 * NIE hinter dem Store zurueckliegen - sonst kaeme eine Bestellung, die bei einem Filterwechsel aus dem
 * Store faellt, beim Rueckwechsel mit dem Stand vom Anmeldezeitpunkt zurueck (Pruefbefund K1).
 */
let hubStand = new Map<number, Order>();

function hubNummer(zeile: Order): number | null {
  const nummer = Number(zeile.jtlOrderId);
  return Number.isSafeInteger(nummer) && nummer > 0 ? nummer : null;
}

function hubStandErsetzen(zeilen: readonly Order[]): void {
  hubStand = new Map();
  hubStandFortschreiben(zeilen, true);
}

function hubStandFortschreiben(zeilen: readonly Order[], auchOhneLauf = false): void {
  if (!hubZeilenLauf && !auchOhneLauf) return;
  for (const zeile of zeilen) {
    const nummer = hubNummer(zeile);
    if (nummer !== null) hubStand.set(nummer, zeile);
  }
}

function hubZeileEntfernen(id: string): void {
  for (const [nummer, zeile] of hubStand) {
    if (zeile.id === id) hubStand.delete(nummer);
  }
}

/** Laufende volle Laeufe je Zustandsauswahl (Schluessel siehe `auswahlSchluessel`). */
const laufendeVollLaeufe = new Map<string, Promise<BestandLadeErgebnis>>();

/** Nur fuer Tests: vergisst Hub-Zeilen und laufende Laeufe. */
export function bestandZwischenspeicherLeeren(): void {
  hubZeilenLauf = null;
  hubStand = new Map();
  laufendeVollLaeufe.clear();
}

/** `undefined` (Server-Vorgabe) und die leere Menge (`state=none`) bekommen verschiedene Schluessel. */
function auswahlSchluessel(states?: BoardStateSelection): string {
  return states === undefined ? "\u0000vorgabe" : `menge:${states.join(",")}`;
}

/**
 * Der volle Lauf. Ruft etwas ihn mit derselben Auswahl, waehrend ein identischer Lauf noch laeuft,
 * haengt es sich an dessen Promise an, statt 25 MB und N Lieferantenabrufe noch einmal zu fahren
 * (Absicherung, Befund aus dem Prod-Accesslog 30.09.2026). Nach Abschluss - Erfolg wie Fehler - ist
 * der Eintrag weg, der naechste Aufruf startet einen neuen Lauf.
 */
function bestandLaden(
  states?: BoardStateSelection,
  optionen: { frisch?: boolean } = {},
): Promise<BestandLadeErgebnis> {
  const schluessel = auswahlSchluessel(states);
  const laufend = laufendeVollLaeufe.get(schluessel);
  // `frisch`: wer gerade Daten VERAENDERT hat (Bestellgruppen), darf sich nicht an einen Lauf haengen,
  // dessen /api/orders VOR der Aenderung abgeschickt wurde.
  if (laufend && !optionen.frisch) return laufend;
  const lauf = bestandLadenEinzeln(states);
  laufendeVollLaeufe.set(schluessel, lauf);
  const aufraeumen = () => {
    if (laufendeVollLaeufe.get(schluessel) === lauf) laufendeVollLaeufe.delete(schluessel);
  };
  lauf.then(aufraeumen, aufraeumen);
  return lauf;
}

function abbruchPruefen(signal: AbortSignal): void {
  if (signal.aborted) throw signal.reason ?? new DOMException("Aborted", "AbortError");
}

/**
 * Hub-Zeilen des Erstlaufs, ueberlagert mit dem, was der Store JETZT fuehrt (`stand`): eine seither
 * geaenderte Bestellung (Nachladen, SSE) soll beim Zustandswechsel nicht auf den Stand des Erstlaufs
 * zurueckfallen. Der Store haelt zusammengefuehrte Zeilen; `zusammenfuehren` uebernimmt daraus nur die
 * Hub-Felder und ueberschreibt alles, was JTL fuehrt, mit der frischen Antwort.
 */
function hubZeilenMitStand(hub: readonly Order[], stand: readonly Order[]): Order[] {
  const nachNummer = new Map<number, Order>();
  for (const zeile of [...hub, ...stand]) {
    const nummer = Number(zeile.jtlOrderId);
    if (Number.isSafeInteger(nummer) && nummer > 0) nachNummer.set(nummer, zeile);
  }
  return [...nachNummer.values()];
}

/**
 * Teillauf fuer einen Zustandswechsel (`SpeicherDienste.bestandTeilLaden`).
 *
 * WAS VOM ZUSTAND ABHAENGT (geprueft, nicht angenommen): (1) `GET /api/jtl/orders?state=...`; (2) die
 * Zusammenfuehrung Hub<->JTL, denn sie braucht die Hub-Zeilen der NEUEN Bestellungen (kommen aus dem
 * lebenden Zwischenspeicher `hubStand`, ueberlagert mit dem Store); (3) die Lieferanten und Freigaben - je Lieferant
 * der sichtbaren Bestellungen, bei `deleted`/`archived` tauchen neue auf. Nachgeholt wird nur, was
 * `aktuell` noch nicht fuehrt; der Rest bleibt stehen. NICHT vom Zustand abhaengig: Board-Spalten,
 * Vorlagen, Zahlungsvorlagen, Kurssicherungen - bleiben unberuehrt (kein Feld im Ergebnis).
 *
 * BEKANNTE GRENZE: Board-Spalten und Freigaben anderer Nutzer werden durch einen Filterwechsel nicht mehr
 * nebenbei aufgefrischt (das SSE-Ereignis `config` hat weder Nachlader noch Store-Feld). Sie kommen mit
 * dem Erstlauf, also bei der naechsten Anmeldung.
 *
 * Ohne Hub-Zeilen des Erstlaufs: voller Lauf.
 */
async function bestandTeilLaden(
  states: BoardStateSelection | undefined,
  kontext: { aktuell: Speicherzustand; signal: AbortSignal; aktuellerStore: () => Speicherzustand },
): Promise<BestandLadeErgebnis> {
  const hubLauf = hubZeilenLauf;
  const { aktuell, signal } = kontext;
  // Ohne Board-Spalten im Store ist der Erstlauf noch nicht uebernommen (oder gescheitert; oder die
  // Konfiguration ist leer - dann laedt jeder Wechsel wie frueher voll): dann lieber der volle,
  // geteilte Lauf statt eines Teillaufs auf unvollstaendigem Stand.
  if (!hubLauf || aktuell.boardSpalten.length === 0) return bestandLaden(states);
  await hubLauf;
  abbruchPruefen(signal);
  // ZUSAMMENFUEHRUNG ERST AM ENDE: waehrend der Abrufe (JTL, Lieferanten, Freigaben) kann der Store sich
  // aendern (SSE, eigene Aenderung oder Loeschung). Hier wird deshalb nur geladen; die Ueberlagerung
  // aus dem lebenden `hubStand` plus dem dann aktuellen Store entsteht zuletzt.
  const jtlRoh = await jtlBestellungenRohLaden(states, signal);
  abbruchPruefen(signal);

  const vorhandene = new Set(aktuell.lieferanten.map((s) => s.id));
  const fehlende = ([...new Set(jtlRoh.map((o) => (o.kLieferant === null ? undefined : String(o.kLieferant))).filter(Boolean))] as string[]).filter(
    (id) => !vorhandene.has(id),
  );
  // Wie im vollen Lauf: ein Lieferant, der sich nicht laden laesst, faellt aus der Liste, wird aber gezaehlt und als
  // EIN Teilfehler gemeldet - vom Speicher, und nur, wenn dieser Lauf uebernommen wird. Wird er vorher abgebrochen,
  // lehnt `abbruchPruefen` unten ab; ein abgelehnter Lauf meldet nichts (L5).
  const neueVersuche = await Promise.all(fehlende.map((id) => versuche(lieferantLaden(id, signal))));
  const neue = neueVersuche.flatMap((v) => (v.ok && v.wert !== null ? [v.wert] : []));
  abbruchPruefen(signal);

  const fehlendeFreigaben = [...new Set(jtlRoh.map((b) => b.kLieferant).filter(Boolean))].filter(
    (k) => !(String(k) in aktuell.freigaben),
  );
  const neueFreigabenPaare = await Promise.all(
    fehlendeFreigaben.map(async (k) => [String(k), await versuche(ladeFreigaben(Number(k), signal))] as const),
  );
  const neueFreigaben = Object.fromEntries(neueFreigabenPaare.map(([k, v]) => [k, v.ok ? v.wert : []] as const));
  abbruchPruefen(signal);
  const jetzt = kontext.aktuellerStore();
  const bestellungen = bestellungenZusammensetzen(
    jtlRoh,
    hubZeilenMitStand([...hubStand.values()], jetzt.bestellungen),
    aktuell.boardSpalten,
  );
  const teilFehler: BestandTeilFehler = {};
  const lieferantenFehler = fehlschlaege(neueVersuche);
  if (lieferantenFehler) teilFehler.lieferanten = lieferantenFehler;
  const freigabenFehler = fehlschlaege(neueFreigabenPaare.map(([, v]) => v));
  if (freigabenFehler) teilFehler.freigaben = freigabenFehler;
  return mitTeilFehler(
    {
      bestellungen,
      lieferanten: [...jetzt.lieferanten, ...neue],
      freigaben: { ...jetzt.freigaben, ...neueFreigaben },
    },
    teilFehler,
  );
}

export const STANDARD_DIENSTE: SpeicherDienste = {
  // Der Wrapper der B40-Reparatur: reicht `aufFehler` an die echte `ereignisseAbonnieren()`
  // (`api/ereignisse.ts`) durch, statt es wie vorher stillschweigend zu verwerfen.
  ereignisseAbonnieren: (aufEreignis, aufFehler, aufLuecke) =>
    ereignisseAbonnieren(aufEreignis, { aufFehler, aufLuecke }),
  nachladen: (entitaet, id) => {
    const lader = NACHLADER.get(entitaet);
    if (!lader) {
      // Bewusst eine Ablehnung und kein stilles `undefined`: `undefined` bedeutet im Reduzierer
      // "Datensatz existiert nicht mehr" und wuerde die Zeile aus der Liste WERFEN. Eine Entitaet,
      // fuer die es (noch) keinen Lader gibt, darf keine Daten loeschen.
      return Promise.reject(new KeinNachladerFehler(entitaet));
    }
    return lader(id);
  },
  bestandLaden,
  bestandTeilLaden,
};

export interface SpeicherWert {
  zustand: Speicherzustand;
  versende: (aktion: Aktion) => void;
  /**
   * Zustandswechsel im Board (Auftrag "Serverseitiger Archivfilter + Ladeanzeige", 2026-08-27),
   * gerufen aus `components/App/App.tsx` bei einer Aenderung der Zustandsauswahl. Laedt seit dem
   * Auftrag "Filterwechsel laedt nicht mehr den ganzen Bestand" (2026-09-30) NUR NOCH, was von der
   * Auswahl abhaengt (`dienste.bestandTeilLaden`); Hub-Zeilen, Konfiguration, Vorlagen und
   * Kurssicherungen bleiben, wie sie sind. Ein noch laufender Teilabruf wird abgebrochen.
   *
   * Setzt `zustand.ladend` waehrend des Laufs (Baustein B) und mischt das Ergebnis wie beim Erstladen
   * ueber `bestandGeladen` in den Zustand. Ohne `dienste.bestandTeilLaden` laeuft der volle Lauf, ohne
   * `dienste.bestandLaden` ist der Aufruf wirkungslos (derselbe Rueckfall wie am Erstlade-Effekt unten).
   * Wer DATEN VERAENDERT hat, die nicht vom Zustand abhaengen (Bestellgruppen), ruft
   * `bestandVollNeuLaden`.
   *
   * Liefert den Ausgang des Laufs (`BestandLaufErgebnis`), damit der Aufrufer reagieren kann: der Haken
   * `useBoardBestellzustand` setzt bei `"gescheitert"` die Filterwahl auf den geladenen Stand zurueck
   * (Entscheidung E13). Scheitert der Lauf, meldet der Store selbst: ohne Stand ueber `bestandLadefehler`
   * (Flaeche in der App-Huelle), mit Stand als Eintrag im Fehlerstapel.
   */
  bestandNeuLaden: (states?: BoardStateSelection) => Promise<BestandLaufErgebnis>;
  /**
   * Der ausdrueckliche VOLLE Neulauf (`dienste.bestandLaden`) - fuer alles, was Hub-Daten veraendert
   * hat und deshalb mehr als den Zustandsfilter neu braucht.
   */
  bestandVollNeuLaden: (states?: BoardStateSelection) => Promise<BestandLaufErgebnis>;
  /**
   * Der BESTAETIGTE Filter (Fix-Runde 1 zu Task 4): die Zustandsauswahl, mit der der Stand im Store zuletzt
   * wirklich GELADEN wurde - gesetzt bei jedem gelungenen, nicht ueberholten Lauf, gleich wer ihn anstiess
   * (Erstladen, Filterwechsel im Board, "Erneut laden" der Ladefehler-Flaeche, Gruppenaktionen). `null`, bis
   * der erste Lauf gelungen ist. Ein Aufruf ohne Auswahl laedt serverseitig "active" und gilt als solcher.
   * Die EINE Wahrheit fuer "welcher Filter passt zur Liste": `useBoardBestellzustand` vergleicht die Wahl
   * der Adresse damit und springt bei einem gescheiterten Wechsel genau dorthin zurueck - nicht auf den
   * zuletzt angeforderten Filter, dessen Lauf vielleicht ueberholt wurde und nie ankam. Eine Funktion statt
   * eines Zustandsfeldes: der Haken liest sie im Timer und im Ergebnis eines Laufs, wo ein Renderwert
   * veraltet sein kann.
   */
  bestaetigterFilter: () => BoardStateSelection | null;
  /**
   * "Neu laden" der Leiste (`zustand.verbindungsluecke`): der volle Lauf mit dem bestaetigten Filter. Gelungen
   * raeumt `laufAbwickeln` die Luecke ab (der Lauf begann nach ihr). Scheitert er, steht die Ursache an der Leiste
   * und KEIN Stapeleintrag kommt dazu (L1 "Kein Doppel"); Abbruch und 401 bleiben still (L5).
   */
  lueckeNeuLaden: () => Promise<BestandLaufErgebnis>;
  /** "Schliessen" der Leiste. Die Anzeige bleibt, wie sie ist; die naechste Luecke bringt die Leiste wieder. */
  lueckeSchliessen: () => void;
}

/**
 * Wie ein Neulade-Lauf (`bestandNeuLaden`, `bestandVollNeuLaden`) ausging (Fehleranzeige Paket 2,
 * Task 4). Das gelieferte Promise lehnt NIE ab - der Fehler ist dann schon behandelt (Flaeche oder
 * Stapeleintrag, siehe `laufAbwickeln`), und `ladefehler`-Flaechen duerfen es als `onErneutLaden`
 * zurueckgeben.
 *
 * - `"geladen"`: der Bestand ist im Store.
 * - `"gescheitert"`: der Lauf ist gescheitert (und gemeldet, sofern es kein Abbruch/401 war).
 * - `"ueberholt"`: ein neuerer Lauf hat diesen abgeloest, oder es gab gar keinen Dienst zum Laden.
 *   Es wurde nichts uebernommen und nichts gemeldet; der Ausgang ist Sache des neueren Laufs.
 */
export type BestandLaufErgebnis = "geladen" | "gescheitert" | "ueberholt";

/** L3-Satz (Spec 2026-10-23): ein Neuladen scheitert, die Anzeige bleibt auf dem vorigen Stand. */
const NEULADEN_GESCHEITERT = "Die Liste konnte nicht neu geladen werden und zeigt noch den vorigen Stand.";

const SpeicherKontext = createContext<SpeicherWert | null>(null);

export interface SpeicherProps {
  dienste?: SpeicherDienste;
  anfangszustand?: Partial<Speicherzustand>;
  children?: ReactNode;
}

/**
 * Trenner im Schluessel der Laufend-Sperre unten. Ein Zeichen, das weder in einem Entitaetsnamen
 * noch in einer id vorkommen kann - mit einem Doppelpunkt ergaeben ("a:b", "c") und ("a", "b:c")
 * denselben Schluessel und der zweite Aufruf unterbliebe.
 */
const SCHLUESSEL_TRENNER = "\u0000";

function anfang(teil: Partial<Speicherzustand> | undefined): Speicherzustand {
  return { ...ANFANGSZUSTAND, ...teil };
}

/*
 * `istZahlungsrolle` und `vorgabeZustaende` (die rollenabhaengige Zustandsmenge des Erstladens) stehen in `lib/bestandQuellen.ts`:
 * der Server des KI-Zugangs braucht dieselbe Regel fuer die Vorgabe seiner rechnenden Werkzeuge. `state/` importiert bewusst kein
 * Modul aus `components/` (daher nicht `RollenAuswahl.tsx#normalisierteRolle`); der Vergleich ist eine einzelne Gleichheitspruefung.
 *
 * Grundlage von Befund 3 (Nachbesserung "Serverseitiger Archivfilter + Ladeanzeige"): die Zahlungsansicht (`UebersichtSeite.tsx`,
 * `istZahlungsansicht`) haelt archivierte Bestellungen mit offener Zahlungsaufgabe absichtlich sichtbar - eine Ausnahme, die nur
 * greift, wenn der Store ueberhaupt archivierte Bestellungen fuehrt. Entscheidung des Auftraggebers: diese Rolle sieht IMMER den
 * vollstaendigen Bestand, unabhaengig von der Board-Zustandswahl. Seit der Umstellung auf die Zustandsmenge (Auftrag 06) heisst das
 * `["active", "archived"]` - OHNE `"deleted"`, denn geloeschte Bestellungen waren in dieser Ausnahme NIE gemeint (das alte `all`
 * deckte nur aktiv+archiviert ab).
 */

 /**
  * Die Zustandsmenge, MIT DER DER ERSTLADELAUF STARTET - EINMAL ermittelt, aus Rolle UND Adresse
  * (Befund K1, Nachbesserung Runde 3; Umstellung auf die Menge in Auftrag 06). Vorher stand hier
  * hart `undefined` (== Server-Vorgabe
  * "active"), unabhaengig davon, was in der Adresse stand - ein Tieflink auf `/board?archiv=archived`
  * (heute `/board?state=archived`) lud deshalb IMMER erst den aktiven Bestand, und ein zweiter,
  * KORRIGIERENDER Lauf aus `App.tsx` (`zielArchiv`-Effekt dort) musste das nachziehen. Weil React
  * Kind-Effekte (dort) vor Eltern-
  * Effekten (hier, `<Speicher>` umschliesst `<Anwendung>`) feuert, zog der korrigierende Lauf dabei
  * IMMER die NIEDRIGERE Folgenummer und der blinde "active"-Lauf hier die HOEHERE - der
  * Letzter-gewinnt-Riegel (`bestandFolge` unten) liess deshalb GARANTIERT den falschen Lauf gewinnen.
  * Sichtbar als: 0 Zeilen, kein Ladehinweis, nur ein manuelles Umschalten heilte es.
  *
  * DER FIX IST NICHT "RATEN, DANN KORRIGIEREN", SONDERN "EINMAL RICHTIG": diese Funktion liest den
  * Zielwert bereits VOR dem ersten Request aus derselben Grammatik, die auch der Router benutzt
  * (`adresse.ts#lesen`) - `App.tsx`s eigener Effekt (`zielArchiv`) muss den Erstladefall seither nicht
  * mehr korrigieren, sondern nur noch echte SPAETERE Filterwechsel nachziehen (siehe dortiger
  * Kommentar).
  *
  * `window.location` DIREKT gelesen, nicht ueber `useLocation()`: dieser Effekt laeuft, bevor
  * `<Anwendung>` (das `useAnsicht()`/den Router benutzt) ueberhaupt gerendert ist, UND `<Speicher>`
  * wird in etlichen Tests OHNE jeden Router-Kontext gerendert (`bestandLaden.test.tsx`,
  * `bestandNeuLaden.test.tsx` u. a.) - ein `useLocation()`-Aufruf hier wuerfe dort "must be used
  * within a Router". `window.location` ist dagegen exakt dieselbe Quelle, an der `<BrowserRouter>`
  * (`main.tsx`) selbst haengt, und wird von denselben Testdateien bereits ueber
  * `window.history.replaceState(...)` VOR dem Rendern gesetzt (Muster: `verdrahtungBoard.test.tsx`).
  *
  * `payment_manager` gewinnt IMMER (`["active", "archived"]`, Befund 3) - unabhaengig von der
  * Adresse, siehe `vorgabeZustaende` (`lib/bestandQuellen.ts`) und den Kommentar oben. Die Adressierung selbst kommt vollstaendig aus
  * `adresse.ts#lesen` (`board.states`) - inklusive Legacy-Rueckfall auf ein altes `archiv`.
  */
 export function ersteZustandswahl(rolle: string | undefined): BoardStateSelection {
   if (istZahlungsrolle(rolle)) return vorgabeZustaende(rolle);
   return lesen(window.location.pathname, window.location.search.replace(/^\?/, "")).board.states;
 }

export function Speicher({ dienste = STANDARD_DIENSTE, anfangszustand, children }: SpeicherProps) {
  const [zustand, versendeRoh] = useReducer(reduzierer, anfangszustand, anfang);
  /**
   * EINE Stelle, an der der Store Hub-Zeilen uebernimmt (Pruefbefund K1): jede Aktion laeuft hier
   * durch. Das Entfernen einer Bestellung (`nachgeladen` mit `null`, auch aus SSE) ist im Store nicht
   * von "faellt aus der Auswahl" zu unterscheiden und wird deshalb HIER an den Zwischenspeicher
   * gemeldet; Aenderungen und Neuanlagen schreibt der Effekt unten aus dem Store fort.
   */
  const versende = useCallback((aktion: Aktion): void => {
    if (
      aktion.art === "nachgeladen" &&
      aktion.entitaet === BESTELLUNG_ENTITAET &&
      (aktion.datensatz === null || aktion.datensatz === undefined)
    ) {
      hubZeileEntfernen(aktion.id);
    }
    versendeRoh(aktion);
  }, []);
  const vorherigeBestellungen = useRef<readonly Order[]>(zustand.bestellungen);
  useEffect(() => {
    const vorher = new Map<string, Order>();
    for (const zeile of vorherigeBestellungen.current) vorher.set(String(zeile.id), zeile);
    vorherigeBestellungen.current = zustand.bestellungen;
    // Nur, was sich seit dem letzten Stand VERAENDERT hat - sonst ueberschriebe ein alter Store-Stand
    // die frischen Zeilen eines vollen Laufs.
    hubStandFortschreiben(zustand.bestellungen.filter((zeile) => vorher.get(String(zeile.id)) !== zeile));
  }, [zustand.bestellungen]);

  /**
   * `angemeldet` ist bewusst ein BOOLEAN und nicht `zustand.nutzer` selbst. Der Nutzerdatensatz
   * bekommt bei jeder Selbstaenderung eine neue Objektidentitaet
   * (`components/App/App.tsx#nutzerAendernUndNeuladen` versendet `bestandGeladen` mit
   * `{ nutzer: geaendert }`) - mit `zustand.nutzer` selbst als Abhaengigkeit holte ein Klick auf
   * "Rolle aendern" sowohl den Anfangsbestand ein zweites Mal ALS AUCH den Ereignisstrom unnoetig
   * neu auf. Was hier zaehlt, ist ausschliesslich der Wechsel `null -> Nutzer` (Anmeldung) und
   * `Nutzer -> null` (Abmeldung, 401): eine erneute Anmeldung laedt/verbindet wieder, eine
   * Aenderung am eigenen Konto nicht. Steht VOR beiden Effekten unten, die daran haengen.
   */
  const angemeldet = zustand.nutzer !== null;

  /**
   * Letzter-gewinnt-Riegel fuer `dienste.bestandLaden()` (Nachbesserung "Serverseitiger Archivfilter
   * + Ladeanzeige", Befund 2). GEMEINSAM fuer BEIDE Aufrufer unten (den Erstlade-Effekt UND
   * `bestandNeuLaden`) - genau EIN Zaehler, keine zwei getrennten: der Befund entstand an der NAHT
   * zwischen beiden, nicht innerhalb eines der beiden allein (siehe Nebenbefund unten).
   *
   * ================= DAS PROBLEM OHNE DIESEN RIEGEL =================
   *
   * Board "Archivierte" waehlen (Serverseite ~1,4 s, 2044 Bestellungen/21460 Positionen), INNERHALB
   * dieses Fensters zurueck auf "Aktive": der schnelle Aktiv-Lauf landet zuerst, der laengst
   * unterwegs gewesene, langsamere Archiv-Lauf antwortet DANACH und ueberschreibt ihn - das Board
   * filtert `!order.archived` ueber den (falschen) Archivbestand und zeigt 0 Zeilen, ohne dass der
   * Ladehinweis das noch anzeigt (`bestellungen` ist ja nicht leer). Nur ein Reload oder erneutes
   * Umschalten heilt das.
   *
   * ================= DER RIEGEL =================
   *
   * Jeder Aufruf zieht VOR dem eigentlichen `laden()`-Aufruf eine eigene, aufsteigende Folgenummer.
   * Wenn seine Antwort eintrifft, gilt sie nur dann noch, wenn seine Nummer der zuletzt GEZOGENEN
   * entspricht - jeder AELTERE Aufruf (auch wenn er FRUEHER antwortet) ist damit per Definition
   * "ueberholt", unabhaengig von der tatsaechlichen Ankunftsreihenfolge. Sowohl die Uebernahme ins
   * `bestandGeladen` ALS AUCH das Zuruecksetzen von `ladend` auf `false` haengen an dieser Pruefung:
   * ohne Letzteres koennte ein ueberholter, aber fruehzeitig fertiger Lauf den Ladehinweis abschalten,
   * waehrend der tatsaechlich massgebliche Lauf noch unterwegs ist.
   *
   * BEHOBEN (Befund K1, Nachbesserung Runde 3, vorher hier als NEBENBEFUND nur benannt): ein
   * direkter Tieflink auf `/board?archiv=archived` loeste zwei VOLLSTAENDIGE `bestandLaden`-Runden
   * aus - der Erstlade-Effekt unten lief BLIND ohne `archiv` (Vorgabe "active"), unmittelbar gefolgt
   * vom Abgleich-Effekt in `App.tsx` (`zielArchiv`), der denselben Zug auf "archived" nachzog. Weil
   * React Kind-Effekte (dort) vor Eltern-Effekten (hier) feuert, zog der KORRIGIERENDE App.tsx-Lauf
   * dabei IMMER die NIEDRIGERE Folgenummer und der blinde "active"-Lauf hier die HOEHERE - dieser
   * Riegel liess deshalb GARANTIERT den falschen Lauf gewinnen, nicht nur gelegentlich. Der Erstlade-
   * Effekt unten liest den Zielfilter seither SELBST aus der Adresse (`ersteZustandswahl` oben, siehe
   * dort), bevor er ueberhaupt den ersten Request abschickt - der App.tsx-Effekt muss den Erstladefall
   * seither nicht mehr korrigieren, sondern zieht nur noch echte SPAETERE Filterwechsel nach.
   */
  const bestandFolge = useRef(0);
  /** Steuerung des laufenden Teilabrufs (`bestandNeuLaden`); ein neuerer Lauf bricht ihn ab. */
  const abbruch = useRef<AbortController | null>(null);
  /** Der jeweils letzte Speicherzustand, fuer `bestandTeilLaden` (`kontext.aktuell`) - ohne Neuaufbau der Callbacks. */
  const zustandStand = useRef(zustand);
  zustandStand.current = zustand;
  /**
   * `true`, sobald ein Bestand uebernommen ist (oder schon im Anfangszustand stand): erst dann gilt ein
   * Fehlschlag als "Neuladen mit Stand" (L3, Stapeleintrag) statt als gescheitertes Erstladen (L1,
   * Flaeche). Bewusst ein eigenes Merkmal und KEIN `bestellungen.length > 0`: ein leerer, aber
   * gelungener Stand ist ein Stand, und eine per Live-Ereignis an die leere Liste gehaengte Bestellung
   * ist keiner - sonst bekaeme der Neuversuch einer Flaeche einen Stapeleintrag zusaetzlich (L1 "Kein Doppel").
   */
  const standUebernommen = useRef(zustand.bestellungen.length > 0);
  /**
   * Der bestaetigte Filter (`SpeicherWert.bestaetigterFilter`): die Auswahl des zuletzt gelungenen,
   * nicht ueberholten Laufs. Eine Referenz, kein Zustandsfeld - der Haken liest sie im Timer und im Ergebnis
   * eines Laufs, dort muss der Wert den Lauf sehen, der gerade geendet hat, nicht den letzten Render.
   */
  const filterBestaetigt = useRef<BoardStateSelection | null>(null);
  const bestaetigterFilter = useCallback((): BoardStateSelection | null => filterBestaetigt.current, []);
  /**
   * Die Folgenummer (`bestandFolge`), die galt, als die Luecke der Live-Verbindung gemeldet wurde; `null`: keine
   * vermerkt (nie gemeldet, geschlossen, abgeraeumt, abgemeldet). Ein gelungener VOLLER Lauf mit hoeherer Nummer
   * hat nach der Luecke begonnen und raeumt sie ab (`laufAbwickeln`). Eine Referenz wie `bestandFolge`: der Strom
   * ruft von aussen, ohne Neuaufbau der Callbacks. Beginnt sie vor dem ersten Stand (Erstladen laeuft noch), bleibt
   * die Leiste aus, bis der Stand da ist - der Lauf selbst begann dann nicht nach ihr und raeumt nichts ab.
   */
  const lueckeFolge = useRef<number | null>(null);

  /**
   * Wertet das Ergebnis EINES Ladelaufs aus - die EINE Stelle fuer Erstlade-Effekt und `bestandNeuLaden`
   * (Fehleranzeige Paket 2, Task 4). Das gelieferte Promise lehnt nie ab. `states` ist die Auswahl, mit der
   * der Lauf geladen wurde (`undefined` = serverseitig "active").
   *
   * - Ein ueberholter Lauf (`eigeneFolge` ist nicht mehr die letzte gezogene Nummer, oder der Aufrufer
   *   hat ihn verworfen) uebernimmt nichts und meldet nichts, auch wenn er scheitert: den Abbruch durch
   *   einen neueren Lauf hat der Nutzer selbst ausgeloest (L5), und der neuere Lauf berichtet.
   * - Gelungen: der Stand kommt in den Store, ein stehender `bestandLadefehler` faellt im selben Dispatch
   *   weg (`bestandLadefehler: null` im Payload - der Reduzierer loescht ihn nirgends sonst), und `states`
   *   ist ab jetzt der bestaetigte Filter. Danach meldet der Speicher die Teilfehler des Laufs
   *   (`teilFehler`: Vorlagen, Lieferanten, Freigaben - je Art ein Stapeleintrag, Task 5). Sie stehen nie im Store
   *   und werden NUR hier gemeldet, also nie von einem gescheiterten oder ueberholten Lauf (L5).
   * - Gescheitert MIT Stand: die Liste bleibt, der Stapel sagt es (L3, `meldeFehler`; Abbruch und 401
   *   ergeben dort `null` und bleiben still). Ausnahme `leiste`: das "Neu laden" der Leiste meldet nicht im Stapel
   *   (L1 "Kein Doppel"), die Ursache steht an der Leiste (`verbindungslueckeUrsache`, nur wenn `fehlertext` sie
   *   nennt - Abbruch und 401 bleiben still). Hat der Nutzer die Leiste inzwischen geschlossen (`lueckeFolge` leer),
   *   hat die Ursache keinen Ort mehr: dann gilt L3 wie sonst, der Stapel meldet.
   * - Gescheitert OHNE Stand: `bestandLadefehler` im Store, die App-Huelle zeigt die Flaeche. Kein
   *   Stapeleintrag (L1 "Kein Doppel"). Ein Abbruch/401 setzt ihn nicht: dort uebernimmt die Anmeldemaske.
   *
   * `art.voll` (Entscheidung R-P2): nur das Erstladen und ein Lauf ueber `dienste.bestandLaden(states, { frisch: true })`.
   * Der Lauf ueber `bestandLaden` OHNE `frisch` (Rueckfall von `bestandNeuLaden` ohne Teildienst) ist es nicht: er darf
   * sich an einen aelteren, vor der Luecke begonnenen Lauf haengen. Nach einem gelungenen Lauf, bei vermerkter Luecke:
   * ein voller Lauf nach ihr raeumt sie ab, jeder andere zeigt sie (eine, die vor dem ersten Stand kam, erst jetzt).
   */
  const laufAbwickeln = useCallback(
    (
      lauf: Promise<BestandLadeErgebnis>,
      eigeneFolge: number,
      states: BoardStateSelection | undefined,
      art: { voll: boolean; leiste?: boolean },
      verworfen: () => boolean = () => false,
    ): Promise<BestandLaufErgebnis> =>
      lauf.then(
        (ergebnis): BestandLaufErgebnis => {
          if (verworfen() || eigeneFolge !== bestandFolge.current) return "ueberholt";
          // Der Teilfehler ist eine Meldung, kein Zustand: er wird vor dem Dispatch herausgeloest.
          const { teilFehler, ...bestand } = ergebnis;
          standUebernommen.current = true;
          filterBestaetigt.current = states ?? ["active"];
          versende({ art: "bestandGeladen", bestand: { ...bestand, bestandLadefehler: null } });
          if (lueckeFolge.current !== null) {
            const erledigt = art.voll && eigeneFolge > lueckeFolge.current;
            if (erledigt) lueckeFolge.current = null;
            versende({ art: erledigt ? "verbindungslueckeErledigt" : "verbindungsluecke" });
          }
          if (teilFehler) meldeBestandTeilFehler(teilFehler);
          return "geladen";
        },
        (ursache: unknown): BestandLaufErgebnis => {
          if (verworfen() || eigeneFolge !== bestandFolge.current) return "ueberholt";
          if (standUebernommen.current) {
            if (!art.leiste || lueckeFolge.current === null) meldeFehler(ursache, NEULADEN_GESCHEITERT);
            else if (fehlertext(ursache) !== null) versende({ art: "verbindungslueckeUrsache", ursache });
          } else if (fehlertext(ursache) !== null) {
            versende({ art: "bestandLadefehler", ursache });
          }
          return "gescheitert";
        },
      ),
    [versende],
  );

  /**
   * Abonnement des Ereignisstroms. HAENGT AN DER ANMELDUNG (Befunde B18/B23, Nachmessung R5) - bis
   * zu diesem Task hing dieser Effekt allein an `[dienste]`. Derselbe Fehler wie beim
   * Anfangsbestand-Effekt unten, an DERSELBEN falschen Abhaengigkeitsliste: `dienste` aendert sich
   * in `STANDARD_DIENSTE` NIE, der Effekt lief also einmalig bei der Montage - VOR jeder Anmeldung,
   * weil `<Speicher>` in `src/App.tsx` auch die Anmeldemaske umschliesst. Zwei Folgen:
   *
   *   B18: der Strom lief OHNE `Authorization`-Kopfzeile los (`standardKopfzeilen()` in
   *   `api/basis.ts` haengt sie nur an, wenn zu diesem Zeitpunkt bereits ein Token gesetzt ist).
   *   B23: bei einem Sitzungsverlust (401, Abmelden) wurde der Strom NIE abgebaut - ein Tab, der
   *   laengst wieder auf der Anmeldemaske steht, bekaeme ueber den weiterhin offenen Strom
   *   Live-Ereignisse zu einer serverseitig nicht mehr existierenden Sitzung.
   *
   * Mit `angemeldet` in der Abhaengigkeitsliste raeumt React die Abbestellfunktion automatisch auf,
   * sobald `angemeldet` auf `false` wechselt (Abbau) - ohne sie ueberlebte der Abonnent zudem jeden
   * Neuaufbau der App (dieselbe Klasse Leck, die das Backend mit `subscriberCount()` absichert, nur
   * auf der Clientseite) - und baut sie bei einer erneuten Anmeldung wieder auf (Neuaufbau).
   *
   * `aufFehler` (Befund B40): ein 401 auf dem Strom selbst (`api/ereignisse.ts` wirft dafuer einen
   * `SitzungsFehler`, siehe dort) ist derselbe Sitzungsverlust wie ein 401 auf jeder anderen
   * Anfrage - der Store meldet sich deshalb selbst ab (`versende({ art: "angemeldet", nutzer: null,
   * ... })`), was wiederum `angemeldet` hier auf `false` zieht und den Strom abbaut. `anfrage()`s
   * eigene 401-Behandlung (`beiSitzungsverlust` in `components/App/App.tsx`) greift hier NICHT: der
   * Strom laeuft ueber ein eigenes `fetch`, nicht ueber `anfrage()`. Jeder ANDERE Fehler (Netz weg,
   * 5xx, ...) bleibt beim eingebauten Wiederverbindungsversuch von `ereignisseAbonnieren` - dafuer
   * gibt es hier nichts weiter zu tun.
   *
   * `aufLuecke` (Lueckenausgleich, Spec 2026-10-24): vermerkt die Folgenummer (`lueckeFolge`) und zeigt die Leiste -
   * aber erst, wenn ein Stand uebernommen ist (H3); davor laeuft das Erstladen noch, oder die Ladefehler-Flaeche
   * steht schon. Der Abbau (Abmeldung) vergisst die Folge.
   */
  useEffect(() => {
    if (!angemeldet) return;
    const abbestellen = dienste.ereignisseAbonnieren(
      (ereignis) => versende({ art: "sse", ereignis }),
      (fehler) => {
        if (fehler instanceof SitzungsFehler) {
          versende({ art: "angemeldet", nutzer: null, rechte: [] });
        }
      },
      () => {
        lueckeFolge.current = bestandFolge.current;
        if (standUebernommen.current) versende({ art: "verbindungsluecke" });
      },
    );
    return () => {
      abbestellen();
      lueckeFolge.current = null;
    };
  }, [dienste, angemeldet]);

  /**
   * Anfangsbestand, falls der Dienst einen liefert (seit Task 1 belegt, siehe SpeicherDienste) -
   * ERST NACH DER ANMELDUNG.
   *
   * DAS "NACH" IST DIE REPARATUR (Gesamtreview 2026-08-11, Critical 1), keine Feinheit. Bis hierher
   * hing dieser Effekt allein an `[dienste]`. Diese Liste aendert sich NIE: `STANDARD_DIENSTE` ist
   * eine modulweite Konstante, und `src/App.tsx` reicht gar kein `dienste`-Prop herein. Der Effekt
   * lief also genau EINMAL bei der Montage - und die liegt VOR jeder Anmeldung, weil `<Speicher>`
   * in `src/App.tsx` auch die Anmeldemaske umschliesst. `api/basis.ts` hielt das Token bis Befund
   * B17 nur im Arbeitsspeicher, keine Sitzung ueberlebte eine Neuladung; der Anfangsbestand wurde
   * deshalb fuer JEDEN Nutzer in JEDER Sitzung mit einem 401 geholt. `anfrageOderNull` lieferte
   * `null`, Zweig 2 lief in dieselbe Wand - Board, Uebersicht, Kosten, Zahlungen und Zeitstrahl
   * zeigten dauerhaft "0 Bestellungen". Gemessen im Ganzseitenlauf zu Task 13 (Ursache A), nicht
   * vermutet.
   *
   * DIE WOERTLICHKEITSREGEL DECKTE DIE ALTE FASSUNG NICHT: das Original laedt ausdruecklich NACH
   * der Sitzung - `await load()` steht im Anmeldezweig (`index.html:5719`) und in
   * `bootstrapSession` (`:5794`), vorher nie. Die alte Reihenfolge war also eine Abweichung, keine
   * uebernommene Eigenheit.
   *
   * `angemeldet` ist oben deklariert - Begruendung dort, sie gilt fuer diesen Effekt ebenso wie
   * fuer den Ereignisstrom-Effekt darueber.
   *
   * DER ZIELWERT KOMMT SEIT BEFUND K1 (Nachbesserung Runde 3) AUS `ersteZustandswahl` (oben) - Rolle
   * UND Adresse, EINMAL ermittelt, BEVOR der erste Request abgeht. Vorher stand hier hart `archiv=all`
   * nur fuer `payment_manager`, sonst `undefined` (Server-Vorgabe "active") - UNABHAENGIG von der
   * Adresse. Das war fuer `payment_manager` (siehe Befund 3, unveraendert weiterhin richtig: diese
   * Rolle sieht IMMER "all", unabhaengig vom Board-Filter) folgenlos, fuer jede andere Rolle mit einem
   * archivierten Tieflink aber der Kern von Befund K1 - siehe der ausfuehrliche Kommentar an
   * `ersteZustandswahl` und an `bestandFolge` oben.
   *
   * `App.tsx`s eigener `zielArchiv`-Effekt liest beim ERSTEN Lauf nach der Anmeldung DIESELBE Formel
   * (`ersteZustandswahl`) und loest deshalb dabei KEINEN zweiten Rundlauf mehr aus - er zieht nur noch
   * echte SPAETERE Filterwechsel nach (siehe dortiger Kommentar). Ein allfaelliger Unterschied
   * zwischen beiden Seiten ist damit ausgeschlossen: beide rechnen mit der IDENTISCHEN Funktion,
   * auf denselben Eingaben (`zustand.nutzer.role`, `window.location`), im selben Rendertakt.
   *
   * `zustand.nutzer?.role` ABSICHTLICH NICHT in der Abhaengigkeitsliste (gelesen, aber nicht
   * gefuehrt) - derselbe Grund, aus dem der Effekt oben `angemeldet` statt `zustand.nutzer` selbst
   * fuehrt (siehe dortige Begruendung): eine SELBSTAENDERUNG der eigenen Rolle waehrend einer
   * laufenden Sitzung soll NICHT automatisch einen zweiten vollstaendigen Erstladelauf ausloesen.
   * Der einmalig beim Anmelden gelesene Wert genuegt fuer die Ermittlung oben; eine spaetere
   * Rollenaenderung erreicht `bestandNeuLaden` ohnehin ueber `App.tsx`, sobald es dort verdrahtet
   * ist (siehe dort).
   */
  useEffect(() => {
    const laden = dienste.bestandLaden;
    if (!laden || !angemeldet) return;
    let verworfen = false;
    // Letzter-gewinnt-Riegel (Befund 2, siehe `bestandFolge` oben) - eigene Folgenummer VOR dem
    // Aufruf gezogen, geteilt mit `bestandNeuLaden` unten.
    abbruch.current?.abort();
    const eigeneFolge = ++bestandFolge.current;
    // `zustand.nutzer?.role`: absichtlich gelesen, aber NICHT in der Abhaengigkeitsliste unten - siehe
    // Kommentar oben ("ABSICHTLICH NICHT in der Abhaengigkeitsliste").
    const states: BoardStateSelection = ersteZustandswahl(zustand.nutzer?.role);
    // Baustein B (Auftrag "Serverseitiger Archivfilter + Ladeanzeige"): `ladend` umklammert auch den
    // Erstladelauf, nicht nur `bestandNeuLaden` unten - der App-weite `<LadeHinweis>` (Auftrag
    // "Schwebender Lade-Hinweis (Spinner-Popup) statt Textzeile", 2026-08-28) haengt direkt an diesem
    // Feld, unabhaengig davon, ob bereits Bestellungen im Store stehen.
    versende({ art: "ladend", wert: true });
    // IMMER MIT EXPLIZITEM `archiv`-Argument (Befund K1, Nachbesserung Runde 3) - vorher stand hier
    // eine Fallunterscheidung, die fuer die Vorgabe "active" bewusst OHNE Argument rief (siehe Git-
    // Historie). Das ist mit `ersteZustandswahl` nicht mehr sinnvoll moeglich (und noetig): die Adresse
    // kann die Vorgabe "active" genauso wollen wie jeden anderen Wert, ein expliziter Aufruf mit
    // `"active"` verhaelt sich serverseitig identisch zu einem Aufruf ganz ohne Parameter
    // (`backend/src/http/routes/jtl.ts`, Vorgabe bei fehlendem `archiv`-Query ist ebenfalls "active").
    // Gelingen, Scheitern (Flaeche, solange noch kein Stand da ist) und "ueberholt" wertet `laufAbwickeln`
    // aus; ein verworfener Lauf (Abmeldung, Abbau) bleibt dabei ebenso still wie ein ueberholter.
    void laufAbwickeln(laden(states), eigeneFolge, states, { voll: true }, () => verworfen).then(() => {
      if (!verworfen && eigeneFolge === bestandFolge.current) {
        versende({ art: "ladend", wert: false });
      }
    });
    return () => {
      verworfen = true;
    };
  }, [dienste, angemeldet]);

  /**
   * `bestandNeuLaden` (SpeicherWert, Auftrag "Serverseitiger Archivfilter + Ladeanzeige") - derselbe
   * Rundlauf wie der Erstlade-Effekt oben (Baustein B: `ladend` waehrend des Laufs, zurueckgesetzt
   * nach Erfolg wie Fehlschlag), aber OHNE an `angemeldet` zu haengen: er wird gezielt von aussen
   * angestossen (Archivfilter-Wechsel im Board), nicht durch einen Zustandswechsel im Store selbst.
   * KEIN `verworfen`-Riegel wie oben - diese Funktion haengt an keinem Effekt-Cleanup, sie lebt nur so
   * lange wie `<Speicher>` selbst.
   */
  const bestandNeuLadenMit = useCallback(
    (states: BoardStateSelection | undefined, teil: boolean, leiste = false): Promise<BestandLaufErgebnis> => {
      const laden = dienste.bestandLaden;
      // Ohne Dienst gibt es keinen Lauf: wirkungslos, wie ein ueberholter Lauf (nichts uebernommen, nichts gemeldet).
      if (!laden) return Promise.resolve<BestandLaufErgebnis>("ueberholt");
      // Ein neuer Lauf, gleich welcher Art, ueberholt den noch laufenden Teilabruf: abbrechen statt
      // 11,6 MB weiterzuladen, deren Ergebnis `bestandFolge` ohnehin verwirft. Der Abbruch selbst ist
      // KEIN Fehler - die Ablehnung landet im stillen Zweig unten, `ladend` gehoert dem neueren Lauf.
      abbruch.current?.abort();
      const steuerung = new AbortController();
      abbruch.current = steuerung;
      // Letzter-gewinnt-Riegel (Befund 2, siehe `bestandFolge` oben) - derselbe geteilte Zaehler wie
      // im Erstlade-Effekt: ein Wechsel "Archivierte" -> "Aktive", der den langsameren Archiv-Lauf
      // ueberholt, darf dessen (dann veraltete) Antwort nicht mehr uebernehmen.
      const eigeneFolge = ++bestandFolge.current;
      versende({ art: "ladend", wert: true });
      const teilLaden = dienste.bestandTeilLaden;
      const lauf =
        teil && teilLaden
          ? teilLaden(states, {
              aktuell: zustandStand.current,
              signal: steuerung.signal,
              aktuellerStore: () => zustandStand.current,
            })
          : teil ? laden(states) : laden(states, { frisch: true });
      // Dieselbe Haltung wie beim Erstladen: kein Automatismus, der bisherige Stand bleibt. Der Abbruch durch
      // einen neueren Lauf landet in "ueberholt" und bleibt still - er ist kein Fehler.
      // `voll` ist, wer `bestandLaden` mit `frisch` ruft (R-P2); `leiste`: das "Neu laden" der Luecken-Leiste.
      return laufAbwickeln(lauf, eigeneFolge, states, { voll: !teil, leiste }).then((ergebnis) => {
        if (abbruch.current === steuerung) abbruch.current = null;
        if (eigeneFolge === bestandFolge.current) versende({ art: "ladend", wert: false });
        return ergebnis;
      });
    },
    [dienste, versende, laufAbwickeln],
  );
  const bestandNeuLaden = useCallback(
    (states?: BoardStateSelection): Promise<BestandLaufErgebnis> => bestandNeuLadenMit(states, true),
    [bestandNeuLadenMit],
  );
  const bestandVollNeuLaden = useCallback(
    (states?: BoardStateSelection): Promise<BestandLaufErgebnis> => bestandNeuLadenMit(states, false),
    [bestandNeuLadenMit],
  );
  const lueckeNeuLaden = useCallback((): Promise<BestandLaufErgebnis> => {
    versende({ art: "verbindungslueckeUrsache", ursache: null });
    return bestandNeuLadenMit(filterBestaetigt.current ?? undefined, false, true);
  }, [versende, bestandNeuLadenMit]);
  const lueckeSchliessen = useCallback((): void => {
    lueckeFolge.current = null;
    versende({ art: "verbindungslueckeErledigt" });
  }, [versende]);

  /**
   * Bereits laufende Nachladeaufrufe. Ohne diese Sperre wuerde der Effekt denselben Datensatz bei
   * jedem Zustandswechsel erneut anfordern, solange er in `nachzuladen` steht - der Eintrag
   * verschwindet ja erst, wenn die Antwort da ist.
   */
  const laufend = useRef<Set<string>>(new Set<string>());

  /**
   * Fuehrt die vom Reduzierer vorgemerkten Nachladeaufrufe aus. Scheitert einer, bleibt der bisherige Stand stehen
   * (B22: eine Ablehnung ist nie "gibt es nicht mehr"), und der Fehlschlag wird gemeldet (Fehleranzeige Paket 2,
   * Task 5) - VOR dem Dispatch `nachladenFehlgeschlagen`, denn der nimmt dem Konflikt die Marke "Uebernahme laeuft",
   * nach der sich die Meldung richtet:
   *
   * - Laeuft fuer den Verweis eine Uebernahme (der Nutzer hat im Konflikt-Banner "neu laden" gedrueckt), meldet der
   *   Effekt `KONFLIKT_STAND_SATZ` per `meldeFehler` - mit Ursache, der Nutzer hat den Knopf gedrueckt. Der Konflikt
   *   bleibt stehen (Reduzierer), ein neuer Klick laedt erneut.
   * - Sonst ist es ein Hintergrundladen, das niemand ausgeloest hat (Ereignis eines Kollegen): `meldeNachladeFehler`
   *   mit `LIVE_NACHLADEN_SATZ` (L4; gleiche Faelle laufen im Stapel ueber den Zaehler zusammen). Eine Ursache, die
   *   selbst zum Wiederholen raet, bleibt dabei weg (`meldeNachladeFehler`): der Satz sagt schon "Bitte neu laden".
   *
   * Abbruch und 401 bleiben still (L5), beide Meldewege fragen `fehlertext`. `bestellungLaden` lehnt kuenftig bei einer
   * gescheiterten JTL-Anreicherung ab (Task 7) und landet damit genau hier.
   *
   * Ebenfalls still, mit abgeraeumtem Bedarf (Fix-Runde 1): eine Entitaet ohne Nachlader (`KeinNachladerFehler`, R-T5b -
   * `config`, `responsibilityModule` und `taskTemplate` sendet das Backend, geladen wird dort nichts) und eine
   * Abmeldung vor dem Fehlschlag (G2 - der Satz stuende sonst auf der Anmeldemaske).
   */
  useEffect(() => {
    for (const verweis of zustand.nachzuladen) {
      const schluessel = verweis.entitaet + SCHLUESSEL_TRENNER + verweis.id;
      if (laufend.current.has(schluessel)) continue;
      laufend.current.add(schluessel);
      void dienste.nachladen(verweis.entitaet, verweis.id).then(
        (datensatz) => {
          laufend.current.delete(schluessel);
          versende({ art: "nachgeladen", entitaet: verweis.entitaet, id: verweis.id, datensatz });
        },
        (ursache: unknown) => {
          laufend.current.delete(schluessel);
          // `zustandStand` ist der Stand des letzten Renders; die Marke wurde mit dem Klick gesetzt und ist, wenn
          // sie noch steht, auch hier noch da (ein neues Fremdereignis ersetzt den Konflikt samt Marke).
          //
          // Zwei Faelle, in denen NICHT gemeldet wird, der Bedarf aber trotzdem abgeraeumt wird (sonst entdoppelte
          // `nachladenVormerken` jedes spaetere Ereignis zu diesem Datensatz weg):
          // - kein Nachlader fuer die Entitaet (R-T5b): es wurde nichts versucht, also ist nichts gescheitert;
          // - abgemeldet (G2): die Meldung stuende sonst auf der Anmeldemaske, `<FehlerStapel/>` liegt ausserhalb.
          if (!(ursache instanceof KeinNachladerFehler) && zustandStand.current.nutzer !== null) {
            if (uebernahmeLaeuft(zustandStand.current.konflikte, verweis)) {
              meldeFehler(ursache, KONFLIKT_STAND_SATZ);
            } else {
              meldeNachladeFehler(ursache, LIVE_NACHLADEN_SATZ);
            }
          }
          versende({ art: "nachladenFehlgeschlagen", entitaet: verweis.entitaet, id: verweis.id });
        },
      );
    }
  }, [zustand.nachzuladen, dienste]);

  return (
    <SpeicherKontext.Provider
      value={{
        zustand,
        versende,
        bestandNeuLaden,
        bestandVollNeuLaden,
        bestaetigterFilter,
        lueckeNeuLaden,
        lueckeSchliessen,
      }}
    >
      {children}
    </SpeicherKontext.Provider>
  );
}

export function useSpeicher(): SpeicherWert {
  const wert = useContext(SpeicherKontext);
  if (!wert) throw new Error("useSpeicher ausserhalb von <Speicher> aufgerufen");
  return wert;
}
